import { valorDaFormula } from './formulaEfeito';

/** Constantes de combate que o servidor pode sobrescrever. `esquemaVersao` sobe só quando a forma muda. */
export const ESQUEMA_VERSAO = 3;

export interface MagiaTabela {
  readonly id: string;
  readonly mana: number;
  readonly danoMin?: number | null;
  readonly danoMax?: number | null;
  readonly alcance: number;
  readonly atraso: number;
  readonly cicloS: number;
  /** A recarga própria, em segundos (techspec-combate-v2). Servidor antigo não manda: 0. */
  readonly cdS?: number;
}

/**
 * Buff ou debuff (techspec-buff-debuff-config): efeito = máx(1, trunc(fórmula)), com {MAGIA} e {AVAL};
 * duração = máx(mínimo, Magia × s por Magia). Night sight não tem efeito (fórmula nula).
 */
export interface EfeitoTabela {
  readonly id: string;
  readonly formula?: string | null;
  readonly duracaoCoef: number;
  readonly duracaoMin: number;
}

export interface RegrasEfeito {
  /** A Resistir Magia do alvo corta Resistir/este da duração do debuff… */
  readonly resistDiv: number;
  /** …até esta fração. */
  readonly resistCorte: number;
  /** Weaken/Clumsy/Curse em monstro. */
  readonly mobFator: number;
  readonly reativaFracao: number;
  readonly archRaio: number;
}

export interface CuraTabela {
  readonly id: string;
  readonly coef: number;
  readonly rolagem: number;
}

/**
 * Os números de combate de um item, do perfil publicado (techspec-combate-centralizado): a dica do item na mochila e
 * no boneco. Só vem o que o item tem.
 */
export interface ItemTabela {
  readonly id: string;
  readonly dado?: string | null;
  readonly danoMin?: number | null;
  readonly danoMax?: number | null;
  readonly velocidade?: number | null;
  readonly alcance?: number | null;
  readonly ar?: number | null;
  readonly forca?: number | null;
  readonly maos?: number | null;
  readonly bonus?: { readonly velAtaque: number; readonly velMagia: number; readonly str: number; readonly dex: number; readonly int: number } | null;
  readonly efeito?: number | null;
  readonly duracaoS?: number | null;
}

export interface TabelaCombate {
  readonly bonusTeto: number;
  readonly pocaoEsperaS: number;
  readonly magias: readonly MagiaTabela[];
  /** Servidor antigo não manda: valem os padrões abaixo. */
  readonly efeitos?: readonly EfeitoTabela[];
  readonly regrasEfeito?: RegrasEfeito;
  readonly cura?: readonly CuraTabela[];
  readonly paralisiaS?: number;
  /** Servidor antigo não manda: a dica do item mostra só nome, peso e durabilidade. */
  readonly itens?: readonly ItemTabela[];
}

/** Idêntico a `TuningSchema.Efeitos` (padrão do UO clássico). */
export const EFEITOS_PADRAO: readonly EfeitoTabela[] = [
  { id: 'clumsy', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'feeblemind', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'weaken', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'curse', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'agility', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'cunning', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'strength', formula: '1 + {MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'bless', formula: '1 + {MAGIA} / 20', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'night-sight', formula: null, duracaoCoef: 0, duracaoMin: 600 },
  { id: 'protection', formula: '{MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'arch-protection', formula: '{MAGIA} / 10', duracaoCoef: 1.2, duracaoMin: 10 },
  { id: 'reactive-armor', formula: '{MAGIA} / 2', duracaoCoef: 0, duracaoMin: 300 },
  { id: 'magic-reflection', formula: '1 + {MAGIA} / 20', duracaoCoef: 0, duracaoMin: 300 }
];
export const REGRAS_EFEITO_PADRAO: RegrasEfeito = { resistDiv: 200, resistCorte: 0.5, mobFator: 0.1, reativaFracao: 0.25, archRaio: 3 };
export const CURA_PADRAO: readonly CuraTabela[] = [
  { id: 'heal', coef: 0.1, rolagem: 3 },
  { id: 'greater-heal', coef: 0.4, rolagem: 10 }
];
export const PARALISIA_PADRAO_S = 5;

/** Idêntico ao `PerfilCombate.Padrao` / `GET /api/console/combate/esquema`. */
export const TABELA_PADRAO: TabelaCombate = {
  bonusTeto: 0.6,
  pocaoEsperaS: 10,
  magias: [
    { id: 'magic-arrow', mana: 4, danoMin: 3, danoMax: 7, alcance: 10, atraso: 0, cicloS: 2.5 },
    { id: 'harm', mana: 6, danoMin: 6, danoMax: 11, alcance: 8, atraso: 0, cicloS: 2.8 },
    { id: 'fireball', mana: 9, danoMin: 8, danoMax: 14, alcance: 10, atraso: 0, cicloS: 3.0 },
    { id: 'lightning', mana: 11, danoMin: 11, danoMax: 18, alcance: 10, atraso: 0, cicloS: 3.3 },
    { id: 'mind-blast', mana: 14, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 3.5 },
    { id: 'paralyze', mana: 14, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 3.5 },
    { id: 'energy-bolt', mana: 20, danoMin: 15, danoMax: 24, alcance: 10, atraso: 0, cicloS: 3.8 },
    { id: 'explosion', mana: 20, danoMin: 15, danoMax: 24, alcance: 10, atraso: 2500, cicloS: 3.8 },
    { id: 'flamestrike', mana: 40, danoMin: 24, danoMax: 36, alcance: 10, atraso: 0, cicloS: 4.0 },
    { id: 'poison', mana: 9, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 3.0 },
    { id: 'cure', mana: 6, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 2.8 },
    { id: 'heal', mana: 4, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 2.5 },
    { id: 'greater-heal', mana: 11, danoMin: 0, danoMax: 0, alcance: 10, atraso: 0, cicloS: 3.3 },
    { id: 'recall', mana: 0, danoMin: 0, danoMax: 0, alcance: 0, atraso: 0, cicloS: 4.5 }
  ]
};

let atual: TabelaCombate = TABELA_PADRAO;
let porItem = new Map<string, ItemTabela>();

export function tabelaAtual(): TabelaCombate {
  return atual;
}

export function aplicarTabela(t: TabelaCombate): void {
  atual = t;
  porItem = new Map((t.itens ?? []).map(i => [i.id, i]));
}

/** Os números de combate do item, ou undefined quando não tem nenhum (ou o servidor ainda não mandou). */
export function fichaDoItem(defId: string): ItemTabela | undefined {
  return porItem.get(defId);
}

// ---- As contas das dicas: as mesmas do servidor (MagiasDeEfeito, CombatMath), com os números publicados.

export function efeitoDe(id: string): EfeitoTabela | undefined {
  return atual.efeitos?.find(e => e.id === id) ?? EFEITOS_PADRAO.find(e => e.id === id);
}

export function regrasEfeito(): RegrasEfeito {
  return atual.regrasEfeito ?? REGRAS_EFEITO_PADRAO;
}

/** O número do buff/debuff com a Magia e a Avaliar Inteligência de quem conjura (atributo, AR, pontos, círculos). */
export function valorDoEfeito(id: string, magia: number, aval = 0): number {
  const e = efeitoDe(id);
  if (!e || !e.formula) return 0;
  return valorDaFormula(e.formula, magia, aval);
}

/** Duração em segundos, antes da Resistir Magia do alvo. */
export function duracaoDoEfeitoS(id: string, magia: number): number {
  const e = efeitoDe(id);
  if (!e) return 0;
  return Math.max(e.duracaoMin, Math.max(0, magia) * e.duracaoCoef);
}

/** Segundos por ponto de Magia (0 = duração fixa). */
export function duracaoPorMagiaS(id: string): number {
  return efeitoDe(id)?.duracaoCoef ?? 0;
}

export function curaDaMagia(id: string): CuraTabela {
  const lista = atual.cura ?? CURA_PADRAO;
  return lista.find(c => c.id === id) ?? CURA_PADRAO.find(c => c.id === id) ?? CURA_PADRAO[0];
}

export function paralisiaS(): number {
  return atual.paralisiaS ?? PARALISIA_PADRAO_S;
}

/** Primeira etag só registra. Igual não dispara. Diferente dispara rebusca. */
export function deveRebuscarNpcs(anterior: string, novo: string): boolean {
  if (!anterior) return false;
  return anterior !== novo;
}

export function versaoDoEtag(etag: string): number {
  const i = etag.indexOf('-');
  const n = Number(i > 0 ? etag.slice(0, i) : etag);
  return Number.isFinite(n) ? n : 0;
}

export function aplicarEsquema(
  servidor: number,
  recarregar: () => void,
  avisar: () => void
): void {
  if (!(servidor > ESQUEMA_VERSAO)) return;
  try {
    const marca = `uoidle.reload.${servidor}`;
    if (sessionStorage.getItem(marca)) {
      avisar();
      return;
    }
    sessionStorage.setItem(marca, '1');
  } catch {
    recarregar();
    return;
  }
  recarregar();
}
